/**
 * Add-profiles popup (PRD §1 "Add profiles", §5.1 #5; redesign § Dialogs — the old inline
 * panel became a dialog).
 *
 * The textarea is sent verbatim as `{input}` — splitting, normalizing and deduping are the
 * server's job. What comes back is a per-line outcome list, rendered 1:1: `added`, `invalid`,
 * `duplicate` and `archived_duplicate`, the last of which offers Restore instead of a repeat add
 * (#8). Invalid lines never block valid ones.
 */

import { useState } from 'react';
import type { AddProfileOutcome, AddProfileResultDTO } from '@lw/shared';
import { describeError } from '../../api/client';
import { Modal, PlusIcon, Spinner } from '../../components';
import { pluralize } from '../../lib/format';
import { useAddProfiles, useRestoreProfile } from './useHomeData';

const OUTCOME_LABEL: Record<AddProfileOutcome, string> = {
  added: 'Added',
  invalid: 'Invalid',
  duplicate: 'Already tracked',
  archived_duplicate: 'Archived',
};

/** Status-dot class per outcome — color on the dot only (redesign § Design language). */
const OUTCOME_STATUS: Record<AddProfileOutcome, string> = {
  added: 'status--ok',
  invalid: 'status--error',
  duplicate: 'status--idle',
  archived_duplicate: 'status--queued',
};

function ResultRow({ result }: { result: AddProfileResultDTO }) {
  const restore = useRestoreProfile();
  const restorableId = result.outcome === 'archived_duplicate' ? result.profileId : undefined;

  return (
    <li className="add-result">
      <span className={`status ${OUTCOME_STATUS[result.outcome]} add-result__outcome`}>
        <span className="status__dot" aria-hidden="true" />
        {OUTCOME_LABEL[result.outcome]}
      </span>
      <span className="add-result__input" title={result.input}>
        {result.input}
      </span>
      <span className="add-result__message">{result.message}</span>
      {restorableId !== undefined ? (
        <button
          type="button"
          className="btn-ghost btn-ghost--sm"
          disabled={restore.isPending || restore.isSuccess}
          onClick={() => restore.mutate(restorableId)}
        >
          {restore.isPending ? <Spinner size="sm" /> : null}
          {restore.isSuccess ? 'Restored' : 'Restore'}
        </button>
      ) : null}
    </li>
  );
}

export type AddProfilesDialogProps = {
  open: boolean;
  onClose: () => void;
};

export function AddProfilesDialog({ open, onClose }: AddProfilesDialogProps) {
  const [input, setInput] = useState('');
  const add = useAddProfiles();
  const results = add.data?.results ?? [];
  const addedCount = add.data?.added.length ?? 0;

  const close = () => {
    setInput('');
    add.reset();
    onClose();
  };

  const submit = () => {
    const trimmed = input.trim();
    if (trimmed.length === 0 || add.isPending) return;
    add.mutate(trimmed, { onSuccess: () => setInput('') });
  };

  return (
    <Modal
      open={open}
      title="Add profiles"
      size="md"
      onClose={close}
      footer={
        <>
          {add.isSuccess ? (
            <span className="muted dialog-hint">
              {pluralize(addedCount, 'profile')} added
              {results.length > addedCount ? ` · ${results.length - addedCount} skipped` : ''}.
            </span>
          ) : null}
          <button
            type="button"
            className="btn-primary"
            onClick={submit}
            disabled={input.trim().length === 0 || add.isPending}
          >
            {add.isPending ? <Spinner size="sm" /> : <PlusIcon />}
            Add
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label className="field__label" htmlFor="add-profiles-input">
            Profile or company URLs
          </label>
          <textarea
            id="add-profiles-input"
            className="textarea"
            rows={5}
            placeholder={
              'https://linkedin.com/in/a-person\n' +
              'https://linkedin.com/company/a-company'
            }
            value={input}
            onChange={(event) => setInput(event.target.value)}
          />
          <span className="field__hint">
            One or many profile or company-page URLs, separated by new lines, commas or
            spaces. Adding never starts a scrape — new profiles begin on Manual.
          </span>
        </div>

        {add.isError ? (
          <p className="form-error" role="alert">
            {describeError(add.error)}
          </p>
        ) : null}

        {results.length > 0 ? (
          <ul className="add-results">
            {results.map((result) => (
              <ResultRow key={`${result.line}-${result.input}`} result={result} />
            ))}
          </ul>
        ) : null}
      </div>
    </Modal>
  );
}

export default AddProfilesDialog;
